import { inject } from '@angular/core';

import { UserLoginEvent, UserLogoutEvent, UserService } from '@frontend/vanilla/core';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { Subscription, filter } from 'rxjs';

import { LobbyItem } from '../casino-lobby-manager/casino-lobby.models';
import { CasinoLobbyService } from '../casino-lobby-manager/casino-lobby.service';
import { ConfigProviderService } from '../config-provider/config-provider.service';
import { TeaserService } from '../shared/teaser.service';
import { NormalTeasersModel, OptimisedTeasersModel, SiteCoreTeasersByLobbyType } from './teasers.model';

type TeasersState = {
    optimisedTeasers: OptimisedTeasersModel;
    normalTeasers: NormalTeasersModel;
    initialised: boolean;
    isOptimizedTeasers: boolean;
    siteCoreTeasersByLobbyTypes: SiteCoreTeasersByLobbyType;
    lobbyLoadSubscription: Subscription;
};

const initialState: TeasersState = {
    optimisedTeasers: {
        globalTeaserSettings: undefined,
        globalTeaserStyles: undefined,
        optimizedTeaserContents: [],
    },
    normalTeasers: {
        teasers: [],
    },
    initialised: false,
    isOptimizedTeasers: false,
    siteCoreTeasersByLobbyTypes: {},
    lobbyLoadSubscription: new Subscription(),
};

export const TeasersStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withMethods((store, teaserService = inject(TeaserService), configProviderService = inject(ConfigProviderService)) => ({
        setTeasersToInitialState() {
            patchState(store, {
                optimisedTeasers: {
                    globalTeaserSettings: undefined,
                    globalTeaserStyles: undefined,
                    optimizedTeaserContents: [],
                },
                normalTeasers: {
                    teasers: [],
                },
                initialised: false,
                isOptimizedTeasers: false,
            });
        },
        setLobbyLoadSubscription(lobbyLoadSubscription: Subscription) {
            patchState(store, {
                lobbyLoadSubscription,
            });
        },
        setNormalTeasers(teasers: any[]) {
            patchState(store, {
                normalTeasers: { teasers },
                initialised: true,
                isOptimizedTeasers: false,
            });
        },
        setOptimisedTeasers(teasersContent: OptimisedTeasersModel) {
            patchState(store, {
                optimisedTeasers: teasersContent,
                initialised: true,
                isOptimizedTeasers: true,
            });
        },

        setSiteCoreTeasersFromLobby(lobbyTypeMaps: Map<string, LobbyItem>) {
            const lobbyTypeKeys = [...lobbyTypeMaps.keys()];
            if (!lobbyTypeKeys || !lobbyTypeKeys.length) {
                return;
            }
            const updatedSiteCoreTeasersByLobbyTypes = { ...store.siteCoreTeasersByLobbyTypes() };
            for (let i = 0; i < lobbyTypeKeys.length; i++) {
                if (!updatedSiteCoreTeasersByLobbyTypes[lobbyTypeKeys[i]]) {
                    updatedSiteCoreTeasersByLobbyTypes[lobbyTypeKeys[i]] = {};
                }
                const lobbyTypeMap = lobbyTypeMaps.get(lobbyTypeKeys[i]);

                const allCategoriesObjs = lobbyTypeMap?.categoriesMap ? [...lobbyTypeMap.categoriesMap.values()] : [];
                if (!allCategoriesObjs || !allCategoriesObjs.length) {
                    continue;
                }
                for (let j = 0; j < allCategoriesObjs.length; j++) {
                    const categoryData = allCategoriesObjs[j];
                    const categoryId = categoryData.categoryid;
                    const catSitecoreData = categoryData.sitecoreData;
                    if (
                        updatedSiteCoreTeasersByLobbyTypes[lobbyTypeKeys[i]][categoryId] &&
                        updatedSiteCoreTeasersByLobbyTypes[lobbyTypeKeys[i]][categoryId].initialised
                    ) {
                        continue;
                    }

                    updatedSiteCoreTeasersByLobbyTypes[lobbyTypeKeys[i]][categoryId] = {
                        teaserData: {
                            categoryId,
                            showTeasers: catSitecoreData.showTeasers,
                            optimisedTeasers: {
                                teasers: catSitecoreData.optimizedTeasers ?? [],
                                miniTeasers: catSitecoreData.optimisedMiniTeasers ?? [],
                            },
                            normalTeasers: catSitecoreData.teasers ?? [],
                        },
                        initialised: true,
                    };
                }
            }
            patchState(store, {
                siteCoreTeasersByLobbyTypes: updatedSiteCoreTeasersByLobbyTypes,
            });
        },

        getTeaserDataFromAPI() {
            teaserService.fetchTeasers().subscribe((response) => {
                if (response?.optimizedTeaserContents) {
                    this.setOptimisedTeasers({
                        optimizedTeaserContents: response.optimizedTeaserContents,
                        globalTeaserSettings: response.globalTeaserSettings,
                        globalTeaserStyles: response.globalTeaserStyles,
                    });
                } else {
                    this.setNormalTeasers(response);
                }
            });
        },
        init() {
            this.setTeasersToInitialState();
            configProviderService.casinoClientConfigObservable.subscribe((isClientConfigLoaded) => {
                if (isClientConfigLoaded) {
                    this.getTeaserDataFromAPI();
                }
            });
        },
    })),
    withHooks((store, user = inject(UserService), casinoLobbyService = inject(CasinoLobbyService)) => ({
        onInit() {
            user.events.pipe(filter((e: any) => e instanceof UserLoginEvent || e instanceof UserLogoutEvent)).subscribe(() => {
                store.init();
            });
            const lobbyLoadSubscription = casinoLobbyService.lobbyLoadedObservable.subscribe((response) => {
                if (response) {
                    store.setSiteCoreTeasersFromLobby(response.lobbyTypeMaps);
                }
            });
            store.setLobbyLoadSubscription(lobbyLoadSubscription);
        },
        onDestroy() {
            store.lobbyLoadSubscription().unsubscribe();
        },
    })),
);
